import { useTranslation } from 'react-i18next';
import { ShoppingCart, Utensils, Minus, Plus, Trash2, CheckCircle2, ShoppingBag, Lock, Sparkles, CreditCard } from 'lucide-react';

const CustomerPortalCart = ({
    cart, tenant, inventory = {}, customer, tableNumber, setTableNumber, urlTableParam,
    allValidPromotions, appliedPromotion, setAppliedPromotion,
    redemptions, isPromotionEligible,
    calculateCartTotal, calculateEarnedPoints,
    handlePayment, handleOnlinePayment, paymentLoading, primaryColor, setActiveTab,
    updateCartQuantity, removeFromCart,
    rewardCode, setRewardCode, appliedReward, handleApplyReward,
    activeRewardCodes = [], activeTab,
    addedCoupons = []
}) => {
    const { t } = useTranslation();

    // Use string comparison to be safe
    const currentTableIdStr = String(tableNumber);
    const urlTableIdStr = urlTableParam ? String(urlTableParam) : null;

    return (
        <div className="fade-in">
            <div className="card glass-premium" style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <ShoppingCart size={24} color={primaryColor} /> {t('portal.cart')}
                </h3>

                {cart.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>
                        <ShoppingCart size={48} style={{ margin: '0 auto 1rem' }} />
                        <p style={{ margin: 0 }}>{t('portal.emptyCart')}</p>
                        <button
                            onClick={() => setActiveTab('menu')}
                            style={{ marginTop: '1rem', background: 'transparent', border: `1px solid ${primaryColor}`, color: primaryColor, padding: '8px 20px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
                        >
                            {t('menu.explore')}
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {cart.map((item) => {
                            const stock = inventory[item.prodId] ?? 999;
                            const isOutOfStock = tenant?.stock_menu_enabled ? (stock !== -1 && stock <= 0) : false;
                            
                            return (
                                <div key={item.prodId} style={{ display: 'grid', gridTemplateColumns: '60px 1fr auto auto', gap: '1rem', alignItems: 'center', opacity: isOutOfStock ? 0.6 : 1 }}>
                                <div style={{ width: '60px', height: '60px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0, background: 'var(--bg-main)' }}>
                                    {item.image ? (
                                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2 }}>
                                            <Utensils size={24} />
                                        </div>
                                    )}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <h4 style={{ fontSize: '0.925rem', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 0 0.25rem 0' }}>{item.name}</h4>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ fontSize: '0.85rem', color: primaryColor, fontWeight: 700 }}>₺ {item.price}</div>
                                        {isOutOfStock && <span style={{ color: '#ef4444', fontSize: '0.65rem', fontWeight: 800 }}>({t('inventory.outOfStock', 'Stokta Yok')})</span>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-main)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                                    <button
                                        onClick={() => updateCartQuantity(item.prodId, -1)}
                                        style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', padding: '4px', cursor: 'pointer', display: 'flex' }}
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span style={{ fontWeight: 800, fontSize: '0.85rem', minWidth: '18px', textAlign: 'center' }}>{item.quantity}</span>
                                    <button
                                        onClick={() => updateCartQuantity(item.prodId, 1)}
                                        style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', padding: '4px', cursor: 'pointer', display: 'flex' }}
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                                <button
                                    onClick={() => removeFromCart(item.prodId)}
                                    style={{ background: 'transparent', border: 'none', color: '#ef4444', padding: '8px', display: 'flex', cursor: 'pointer' }}
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        )})}

                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px dashed var(--border)' }}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-muted)' }}>
                                    {t('portal.tableNumber')}
                                </label>
                                <select
                                    value={currentTableIdStr}
                                    onChange={(e) => setTableNumber(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border)',
                                        background: 'var(--bg-main)',
                                        fontSize: '0.9375rem',
                                        fontWeight: 600,
                                        color: 'var(--text-main)',
                                        outline: 'none',
                                        marginBottom: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {urlTableIdStr ? (
                                        <>
                                            {tenant?.tables?.find(t => String(t.id) === urlTableIdStr) ? (
                                                <option value={urlTableIdStr}>{tenant.tables.find(t => String(t.id) === urlTableIdStr).name}</option>
                                            ) : (
                                                <option value={urlTableIdStr}>Masa {urlTableIdStr}</option>
                                            )}
                                            <option value="GelAl">Gel Al</option>
                                            <option value="Paket">Paket Servis</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="GelAl">Gel Al</option>
                                            <option value="Paket">Paket Servis</option>
                                        </>
                                    )}
                                </select>



                                {/* Reward Code Input */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-muted)' }}>
                                        {t('portal.rewardCode', 'Ödül / Kupon Kodu')}
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="text"
                                            value={rewardCode}
                                            onChange={(e) => setRewardCode(e.target.value.toUpperCase())}
                                            placeholder="XXXXXX"
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border)',
                                                background: 'var(--bg-main)',
                                                fontSize: '0.9375rem',
                                                fontWeight: 800,
                                                letterSpacing: '2px',
                                                outline: 'none'
                                            }}
                                        />
                                        <button
                                            onClick={() => handleApplyReward(rewardCode)}
                                            style={{
                                                padding: '0 20px',
                                                background: primaryColor,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '12px',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Uygula
                                        </button>
                                    </div>
                                    {appliedReward && (
                                        <div className="fade-in" style={{
                                            marginTop: '12px',
                                            background: '#f0fdf4',
                                            border: '1px solid #bbfcbd',
                                            padding: '12px',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <CheckCircle2 size={18} color="#16a34a" />
                                                <div>
                                                    <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: '#16a34a' }}>
                                                        {appliedReward.discount.title} Uygulandı!
                                                    </div>
                                                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#15803d' }}>
                                                        Kod: {appliedReward.interaction.code}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleApplyReward(null)} // Or add a removeReward prop
                                                style={{ background: 'transparent', border: 'none', color: '#16a34a', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', padding: '4px 8px' }}
                                            >
                                                Kaldır
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Promotions in Cart */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-muted)' }}>
                                        {t('portal.availablePromotions', 'Mevcut Kampanya & Kuponlar')}
                                    </label>

                                    {/* Active Reward Codes + Added Coupons */}
                                    {(activeRewardCodes.length > 0 || addedCoupons.length > 0) && (
                                        <div style={{
                                            marginBottom: '1rem',
                                            background: primaryColor + '05',
                                            borderRadius: '16px',
                                            padding: '1rem',
                                            border: `1px dashed ${primaryColor}40`
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
                                                <Sparkles size={16} color={primaryColor} />
                                                <span style={{ fontSize: '0.85rem', fontWeight: 800 }}>{t('portal.activeCoupons', 'Aktif Kuponlarınız')}</span>
                                            </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                {activeRewardCodes.filter(it => it.status === 'active').map(it => {
                                                    const isActive = appliedReward?.interaction?.id === it.id;
                                                    return (
                                                        <div key={it.id} onClick={() => {
                                                            if (isActive) {
                                                                handleApplyReward(null);
                                                            } else {
                                                                handleApplyReward(it.code);
                                                            }
                                                        }} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            background: isActive ? primaryColor + '10' : 'white',
                                                            padding: '8px 12px',
                                                            borderRadius: '12px',
                                                            border: `1px solid ${isActive ? primaryColor : 'var(--border)'}`,
                                                            cursor: 'pointer',
                                                        }}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {it.content?.includes(': ') ? it.content.split(': ')[1].split(' (')[0] : it.content}
                                                                </div>
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.65rem',
                                                                fontWeight: 800,
                                                                padding: '4px 10px',
                                                                borderRadius: '6px',
                                                                marginLeft: '0.5rem',
                                                                flexShrink: 0,
                                                                background: isActive ? primaryColor : '#22c55e15',
                                                                color: isActive ? 'white' : '#16a34a',
                                                            }}>
                                                                {isActive ? 'KULLANILIYOR' : 'KULLAN'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {/* Eklenen kuponlar */}
                                                {addedCoupons.map(coupon => {
                                                    const { eligible, reason } = isPromotionEligible(coupon, cart, customer, redemptions);
                                                    const isActive = appliedPromotion?.id === coupon.id;
                                                    return (
                                                        <div key={coupon.id} onClick={() => {
                                                            if (!eligible && !isActive) return;
                                                            setAppliedPromotion(isActive ? null : coupon);
                                                        }} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            background: isActive ? '#dbeafe' : 'white',
                                                            padding: '8px 12px',
                                                            borderRadius: '12px',
                                                            border: `1px ${isActive ? 'solid #3b82f6' : 'dashed #93c5fd'}`,
                                                            cursor: eligible || isActive ? 'pointer' : 'not-allowed',
                                                            opacity: eligible || isActive ? 1 : 0.6
                                                        }}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {coupon.title}
                                                                </div>
                                                                {!eligible && !isActive && (
                                                                    <div style={{ fontSize: '0.6rem', color: '#ef4444', fontWeight: 600, marginTop: '2px' }}>{reason}</div>
                                                                )}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.65rem',
                                                                fontWeight: 800,
                                                                padding: '4px 8px',
                                                                borderRadius: '6px',
                                                                marginLeft: '0.5rem',
                                                                flexShrink: 0,
                                                                background: isActive ? '#3b82f6' : (eligible ? '#dbeafe' : '#fee2e2'),
                                                                color: isActive ? 'white' : (eligible ? '#1d4ed8' : '#ef4444'),
                                                            }}>
                                                                {isActive ? 'AKTİF' : (eligible ? 'KULLAN' : 'UYGUN DEĞİL')}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* İndirim Kartları - sadece kupon olmayanlar */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '12px',
                                        overflowX: 'auto',
                                        paddingBottom: '8px',
                                        scrollbarWidth: 'none'
                                    }}>
                                        {(() => {
                                            const nonCouponPromos = allValidPromotions.filter(p => p.campaignType !== 'coupon' && p.campaignType !== 'coupon_bundle');
                                            return nonCouponPromos.sort((a, b) => {
                                                const aEligible = isPromotionEligible(a, cart, customer, redemptions).eligible ? 0 : 1;
                                                const bEligible = isPromotionEligible(b, cart, customer, redemptions).eligible ? 0 : 1;
                                                return aEligible - bEligible;
                                            });
                                        })().length === 0 && addedCoupons.length === 0 ? (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                                Kullanılabilir indirim bulunmuyor.
                                            </div>
                                        ) : (
                                            (() => {
                                                const nonCouponPromos = allValidPromotions.filter(p => p.campaignType !== 'coupon' && p.campaignType !== 'coupon_bundle');
                                                return nonCouponPromos.sort((a, b) => {
                                                    const aEligible = isPromotionEligible(a, cart, customer, redemptions).eligible ? 0 : 1;
                                                    const bEligible = isPromotionEligible(b, cart, customer, redemptions).eligible ? 0 : 1;
                                                    return aEligible - bEligible;
                                                });
                                            })().map(promo => {
                                                const { eligible, reason } = isPromotionEligible(promo, cart, customer, redemptions);
                                                const isActive = appliedPromotion?.id === promo.id;
                                                return (
                                                    <div
                                                        key={promo.id}
                                                        onClick={() => {
                                                            if (!eligible && !isActive) return;
                                                            setAppliedPromotion(isActive ? null : promo);
                                                        }}
                                                        style={{
                                                            flexShrink: 0,
                                                            width: '160px',
                                                            padding: '12px',
                                                            borderRadius: '16px',
                                                            background: isActive ? primaryColor + '10' : 'var(--bg-main)',
                                                            border: `2px solid ${isActive ? primaryColor : (eligible ? primaryColor + '40' : 'var(--border)')}`,
                                                            cursor: eligible || isActive ? 'pointer' : 'not-allowed',
                                                            transition: 'all 0.2s',
                                                            position: 'relative',
                                                            opacity: eligible || isActive ? 1 : 0.5,
                                                            boxShadow: eligible && !isActive ? `0 4px 12px ${primaryColor}15` : 'none'
                                                        }}
                                                    >
                                                        {isActive && (
                                                            <div style={{ position: 'absolute', top: '-8px', right: '-8px', background: primaryColor, color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <CheckCircle2 size={12} />
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: primaryColor, marginBottom: '4px' }}>
                                                            İNDİRİM
                                                        </div>
                                                        <div style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '4px', lineHeight: 1.2, height: '2.4em', overflow: 'hidden' }}>
                                                            {promo.title}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 800,
                                                            background: isActive ? 'white' : (eligible ? primaryColor : '#fee2e2'),
                                                            color: isActive ? primaryColor : (eligible ? 'white' : '#ef4444'),
                                                            padding: '6px 8px',
                                                            borderRadius: '8px',
                                                            textAlign: 'center',
                                                            marginTop: '4px',
                                                            border: isActive ? `1px solid ${primaryColor}` : 'none',
                                                            boxShadow: eligible && !isActive ? `0 2px 6px ${primaryColor}40` : 'none'
                                                        }}>
                                                            {isActive ? 'KULLANILIYOR' : (eligible ? 'KULLAN' : 'ŞARTLAR UYGUN DEĞİL')}
                                                        </div>
                                                        {!eligible && !isActive && (
                                                            <div style={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 600, marginTop: '4px', textAlign: 'center' }}>
                                                                {reason}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-muted)' }}>Ara Toplam</span>
                                    <span style={{ fontSize: '1rem', fontWeight: 700 }}>₺ {cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0)}</span>
                                </div>
                                {(() => {
                                    const summary = calculateCartTotal();
                                    const { total, subtotal, promoDiscount, promoDetails, rewardDiscount, rewardDetails } = summary;

                                    return (
                                        <>
                                            {/* Promotion Display */}
                                            {appliedPromotion && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {appliedPromotion.type === 'multiplier' ? (() => {
                                                        const totalPoints = calculateEarnedPoints(total);
                                                        const baseMultiplier = Number(tenant?.loyaltyRules?.purchasePointsMultiplier ?? 0.1);
                                                        const basePoints = Math.floor(total * baseMultiplier);
                                                        const extraPoints = totalPoints - basePoints;
                                                        if (extraPoints > 0) {
                                                            return (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#8b5cf6' }}>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Kampanya ({appliedPromotion.title})</span>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>+{extraPoints} Ekstra Puan</span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })() : (
                                                        <>
                                                            {promoDetails.length > 0 ? promoDetails.map((detail, idx) => (
                                                                <div key={`promo-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#22c55e' }}>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                                                        {detail.name} (İndirim)
                                                                    </span>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>-₺ {detail.amount}</span>
                                                                </div>
                                                            )) : promoDiscount > 0 && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#22c55e' }}>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>İndirim ({appliedPromotion.title})</span>
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>-₺ {promoDiscount}</span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* Reward Display */}
                                            {appliedReward && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                                    {rewardDetails.length > 0 ? rewardDetails.map((detail, idx) => (
                                                        <div key={`reward-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#eab308' }}>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                                                {detail.name} (Ödül)
                                                            </span>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>-₺ {detail.amount}</span>
                                                        </div>
                                                    )) : rewardDiscount > 0 ? (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#eab308' }}>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Ödül ({appliedReward.discount.title})</span>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>-₺ {rewardDiscount}</span>
                                                        </div>
                                                    ) : (
                                                        <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 700, textAlign: 'right' }}>
                                                            Kupon geçerli bir ürün bulunamadı.
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Total */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                                                <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-main)' }}>{t('portal.total')}</span>
                                                <span style={{ fontSize: '1.5rem', fontWeight: 900, color: primaryColor }}>₺ {total}</span>
                                            </div>

                                            {/* Earned Points */}
                                            {(tenant?.loyaltyRules?.purchasePointsEnabled !== false) && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    background: primaryColor + '08',
                                                    padding: '8px 12px',
                                                    borderRadius: '10px',
                                                    marginTop: '4px',
                                                    border: `1px dashed ${primaryColor}40`
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Sparkles size={14} style={{ color: '#f59e0b' }} />
                                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Kazanılacak Puan</span>
                                                    </div>
                                                    <span style={{ fontSize: '0.875rem', fontWeight: 800, color: primaryColor }}>
                                                        +{calculateEarnedPoints(total)} Puan
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Payment Buttons */}
                            {(() => {
                                const hasOnlinePayment = tenant?.integrations?.iyzico?.enabled || tenant?.integrations?.paytr?.enabled || tenant?.integrations?.param?.enabled;
                                const providerName = tenant?.integrations?.iyzico?.enabled ? 'iyzico' : (tenant?.integrations?.paytr?.enabled ? 'PayTR' : 'Param');
                                
                                let providerGradient = 'linear-gradient(135deg, #1a1f71, #2557d6)'; // iyzico default
                                if (tenant?.integrations?.paytr?.enabled && !tenant?.integrations?.iyzico?.enabled) {
                                    providerGradient = 'linear-gradient(135deg, #0055ff, #00b4d8)';
                                } else if (tenant?.integrations?.param?.enabled && !tenant?.integrations?.iyzico?.enabled && !tenant?.integrations?.paytr?.enabled) {
                                    providerGradient = 'linear-gradient(135deg, #6b21a8, #c026d3)';
                                }
                                const isDisabled = paymentLoading || (tenant?.stock_menu_enabled && cart.some(item => { const s = inventory[item.prodId] ?? 999; return s !== -1 && s <= 0; }));

                                return hasOnlinePayment ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {/* Online Payment Button */}
                                        <button
                                            onClick={handleOnlinePayment}
                                            disabled={isDisabled}
                                            style={{
                                                width: '100%',
                                                padding: '16px',
                                                background: providerGradient,
                                                color: 'white',
                                                borderRadius: '16px',
                                                border: 'none',
                                                fontWeight: 800,
                                                fontSize: '1rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '12px',
                                                boxShadow: '0 8px 25px rgba(0, 40, 120, 0.3)',
                                                transition: 'all 0.2s',
                                                opacity: paymentLoading ? 0.7 : 1,
                                                cursor: paymentLoading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {paymentLoading ? (
                                                <div className="loading-spinner" style={{ width: '20px', height: '20px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                            ) : (
                                                <>
                                                    <CreditCard size={20} />
                                                    Kartla Ode
                                                </>
                                            )}
                                        </button>

                                        {/* Regular Order Button (pay at table) */}
                                        <button
                                            onClick={handlePayment}
                                            disabled={isDisabled}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                background: 'var(--bg-main)',
                                                color: 'var(--text-main)',
                                                borderRadius: '16px',
                                                border: '2px solid var(--border)',
                                                fontWeight: 700,
                                                fontSize: '0.9375rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '10px',
                                                transition: 'all 0.2s',
                                                opacity: paymentLoading ? 0.7 : 1,
                                                cursor: paymentLoading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <ShoppingBag size={18} />
                                            Masada Ode
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handlePayment}
                                        disabled={isDisabled}
                                        style={{
                                            width: '100%',
                                            padding: '16px',
                                            background: primaryColor,
                                            color: 'var(--text-on-primary)',
                                            borderRadius: '16px',
                                            border: 'none',
                                            fontWeight: 800,
                                            fontSize: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '12px',
                                            boxShadow: `0 8px 25px ${primaryColor}40`,
                                            transition: 'all 0.2s',
                                            opacity: paymentLoading ? 0.7 : 1,
                                            cursor: paymentLoading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {paymentLoading ? (
                                            <div className="loading-spinner" style={{ width: '20px', height: '20px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        ) : (
                                            <>
                                                <ShoppingBag size={20} />
                                                {t('portal.checkout', 'Siparis Ver')}
                                            </>
                                        )}
                                    </button>
                                );
                            })()}

                            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                    <Lock size={12} />
                                    {tenant?.integrations?.iyzico?.enabled ? (
                                        <span>iyzico ile guvenli odeme</span>
                                    ) : tenant?.integrations?.paytr?.enabled ? (
                                        <span>PayTR ile guvenli odeme</span>
                                    ) : tenant?.integrations?.param?.enabled ? (
                                        <span>Param ile guvenli odeme</span>
                                    ) : (
                                        <span>Guvenli Siparis</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CustomerPortalCart;
